import React, { useState, useEffect } from 'react';
import InputErrorContainer from '../InputErrorContainer';
import Button from '../Button';
import { ReactComponent as EyeOpened } from '../../assets/icons/svg/shared/ic-shared-eye-opened.svg';
import { ReactComponent as EyeClosed } from '../../assets/icons/svg/shared/ic-shared-eye-closed.svg';
import { INPUT_REPLACE_TYPES } from './constants';
import { convertValueToUSD } from '../../helpers/currency.helper';

export interface IProps {
  title: string;
  type: string;
  error: string;
  autocomplete?: string;
  validation: RegExp | string;
  replace?: RegExp | string;
  shouldValidate: boolean;
  onChange?: (data: {
    key: string;
    value: string;
    validation: RegExp | string;
    optional?: boolean;
    internalError?: string;
  }) => void;
  resetError?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  isError?: boolean;
  id: string;
  maxLen?: number;
  defaultValue?: any;
  disabled?: boolean;
  shouldFormatOnMount?: boolean;
  shouldCleanValue?: boolean;
  isOptional?: boolean;
  outerValue?: any;
  maxPrice?: number;
}

export const Input = ({
  title,
  type,
  validation,
  autocomplete,
  error,
  onChange,
  onFocus,
  onBlur,
  shouldValidate,
  isError,
  resetError,
  id,
  maxLen,
  replace,
  defaultValue,
  disabled,
  shouldFormatOnMount,
  shouldCleanValue,
  isOptional,
  outerValue,
  maxPrice,
}: IProps) => {
  const [isFormError, setFormError] = useState(false);
  const [value, setValue] = useState('');
  const [internalError, setInternalError] = useState<string>('');
  const [isOnFocus, setIsOnFocus] = useState<boolean>(false);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const setInputType = (t: string, showPass: boolean) => {
    if (t === 'password') {
      if (showPass) {
        return 'text';
      }
    }
    return t;
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalError('');
    let isInternalError = '';
    let inputValue = e.target.value;
    if (replace) {
      if (replace === INPUT_REPLACE_TYPES.PHONE) {
        const cleanedValue = inputValue.replace(/\D/g, '');
        const match = cleanedValue.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
          inputValue = inputValue.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
        } else {
          inputValue = cleanedValue;
        }
      } else if (replace === INPUT_REPLACE_TYPES.CURRENCY) {
        if (value === '$0.00') {
          if (inputValue !== '$0.000') {
            inputValue = inputValue.substring(inputValue.length - 1);
          }
        }
        const cleanValue = inputValue.replace(/[^0-9.]+/g, '');
        if (!cleanValue.match(/^\d*\.?\d{0,2}$/)) return;
        if (cleanValue.includes('.')) {
          const splittedValue = cleanValue.split('.');
          if (maxPrice && Number(cleanValue) > maxPrice) {
            setInternalError(`Maximum amount should be up to ${convertValueToUSD(maxPrice, 0)}`);
            isInternalError = 'true';
          }
          inputValue = `${convertValueToUSD(Number(splittedValue[0]), 0)}.${splittedValue[1]}`;
        } else {
          if (maxPrice && Number(cleanValue) > maxPrice) {
            setInternalError(`Maximum amount should be up to ${convertValueToUSD(maxPrice, 0)}`);
            isInternalError = 'true';
          }
          inputValue = convertValueToUSD(Number(cleanValue), 0);
        }
      } else if (replace === INPUT_REPLACE_TYPES.PERCENT) {
        const cleanValue = inputValue.replace(/[^0-9.]+/g, '');
        if (!cleanValue.match(/^\d*\.?\d{0,2}$/)) return;
        if (maxPrice && Number(cleanValue) > maxPrice) {
          setInternalError(`Discount cannot exceed %${maxPrice}`);
          isInternalError = 'true';
        }
        if (inputValue === value.replace('%', '') || inputValue.length < value.length) {
          inputValue = `${cleanValue}`;
          inputValue = cleanValue.substring(0, cleanValue.length - 1);
          inputValue += '%';
        } else {
          inputValue = `${cleanValue}%`;
        }
      } else if (replace === INPUT_REPLACE_TYPES.TRIM) {
        inputValue = inputValue.trimStart();
      } else {
        inputValue = inputValue.replaceAll(replace, '');
      }
    }
    setFormError(false);
    setValue(inputValue);
    if (onChange) {
      onChange({
        key: type,
        value: inputValue,
        validation,
        optional: isOptional,
        internalError: isInternalError,
      });
    }
  };

  useEffect(() => {
    if (outerValue) {
      setValue(outerValue);
    }
  }, [outerValue]);

  const validateValue = () => {
    setIsOnFocus(false);
    let isValid: boolean = false;
    if (isOptional && !value) {
      isValid = true;
    } else {
      isValid = new RegExp(validation).test(value);
    }
    if (!isValid) {
      setFormError(true);
    }
    if (onBlur) {
      onBlur();
    }
  };

  const handleBlur = () => {
    if (replace === INPUT_REPLACE_TYPES.CURRENCY && value) {
      const cleanedValue = value.toString().replace(/[^0-9.]+/g, '');
      setValue(convertValueToUSD(Number(cleanedValue), 2));
    }
    validateValue();
  };

  const handleFocus = () => {
    setInternalError('');
    setIsOnFocus(true);
    if (onFocus) {
      onFocus();
    }
  };

  useEffect(() => {
    if (resetError) {
      setFormError(false);
      setInternalError('');
    }
  });

  useEffect(() => {
    if (shouldFormatOnMount && replace) {
      if (replace === INPUT_REPLACE_TYPES.PHONE) {
        const cleanedValue = defaultValue.toString().replace(/\D/g, '');
        const match = cleanedValue.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
          defaultValue = defaultValue.toString().replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
        } else {
          defaultValue = cleanedValue;
        }
      } else if (replace === INPUT_REPLACE_TYPES.CURRENCY) {
        const cleanValue = defaultValue.toString().replace(/[^0-9.]+/g, '');
        if (!cleanValue.match(/^\d*\.?\d{0,2}$/)) return;
        if (cleanValue.includes('.')) {
          const splittedValue = cleanValue.split('.');
          defaultValue = `${convertValueToUSD(Number(splittedValue[0]), 0)}.${splittedValue[1]}`;
        } else {
          defaultValue = convertValueToUSD(Number(cleanValue), 2);
        }
      } else {
        defaultValue = defaultValue.toString().replace(replace, '');
      }
    }
    if (shouldFormatOnMount && maxPrice) {
      if (!defaultValue) return;
      const cleanValue = defaultValue.toString().replace(/[^0-9.]+/g, '');
      const intValue = Number(cleanValue);
      if (maxPrice < intValue) {
        setInternalError(`Maximum amount should be up to ${convertValueToUSD(maxPrice, 0)}`);
      }
    }
  }, []);

  useEffect(() => {
    if (!shouldValidate) return;
    validateValue();
  }, [shouldValidate]);

  useEffect(() => {
    if (!shouldCleanValue) return;
    setValue('');
    setFormError(false);
    setInternalError('');
  }, [shouldCleanValue]);

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
      if (onChange) onChange({ key: type, value: defaultValue, validation });
    }
  }, [defaultValue]);

  useEffect(() => {
    if (internalError) {
      if (onChange) {
        onChange({
          key: type,
          value,
          validation,
          optional: isOptional,
          internalError,
        });
      }
    }
  }, [internalError]);

  return (
    <>
      <div className='floating-input mb-1 relative'>
        <input
          type={setInputType(type, showPassword)}
          id={id}
          className={`${disabled ? 'text-bw05' : ''} border border-bw03 focus:outline-none rounded-md focus:border-action03 focus:shadow-sm hover:bg-bw01 w-full p-3 h-16 ${type === 'password' ? 'pr-[2.75rem]' : ''}`}
          placeholder='transparent placeholder'
          autoComplete={autocomplete || 'off'}
          onChange={handleOnChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          maxLength={maxLen}
          value={value}
          disabled={disabled}
        />
        <label
          id={`inputLabel-${id}`}
          htmlFor={type}
          style={{ color: isOnFocus ? '#0E5CF4' : '#A8B2C8' }}
          className='absolute top-0 left-0 px-3 py-5 h-full pointer-events-none font-metric transform origin-left transition-all duration-100 ease-in-out'
        >
          {title}
        </label>
        {type === 'password' && (
          <Button
            id={`showHidePassword-${id}`}
            className='absolute right-0 mr-[14px] mt-5'
            onClick={() => {
              setShowPassword(!showPassword);
            }}
            childElement={showPassword ? <EyeClosed className='text-bw07' /> : <EyeOpened className='text-bw07' />}
          />
        )}
      </div>
      {error && (
        <InputErrorContainer
          id={`InputComponentError-${id}`}
          className='mb-[2rem]'
          errorMessage={isFormError || isError ? error : ''}
        />
      )}
      {internalError && !isError && (
        <InputErrorContainer
          id={`InputComponentError-${id}`}
          className='mb-[2rem] fadeIn'
          errorMessage={internalError}
        />
      )}
    </>
  );
};
